import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Edit2, Save, X, Trash2, FileText, Download, CheckCircle2, XCircle, Clock, MapPin, Link, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import { MaterialTagForm } from '@/components/MaterialTagForm';
import { ReceiveSheet } from '@/components/ReceiveSheet';
import { ClipTagsPanel } from '@/components/ClipTagsPanel';
import '@/styles/receiving.css';
import '@/styles/tag-mark.css';
import { TagMark } from '@/components/TagMark';

// PR-1 — the five places a delivery can be, as a person says them.
const RECEIVE_WHERE = { office: 'the office', site: 'site', warehouse: 'the warehouse',
  vendor: 'the vendor holds it', contractor: 'the contractor took it' };
// PR-1b — the Received row is a fact, so it reads as one: the day, the place,
// the spot, and the count only when more than one was expected.
const RECEIVE_PLACE = { office: 'Office', site: 'Site', warehouse: 'Warehouse',
  vendor: 'Vendor holds', contractor: 'Contractor' };
const receivedLine = (r) => {
  const day = r.received_at
    ? new Date(r.received_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const bits = [`Received ${day}`.trim(), RECEIVE_PLACE[r.where] || r.where];
  if (r.location) bits.push(r.location);
  if (Number(r.of) > 1) bits.push(`${r.count} of ${r.of}`);
  return bits.filter(Boolean).join(' · ');
};
import { ClipStar, EstimateTick } from '@/components/ClipCard';
import { getCurrentUserId } from '@/utils/currentUser';
import '@/styles/site-office.css';
import '@/styles/spec-sheet.css';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import RoomPicker from '@/components/rooms/RoomPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// CS-3a — the facts row's dimensions, read the same way wherever a document
// prints them: the typed field if there is one, else width · depth · height.
export const clipDimensions = (clip) => clip.dimensions
  || [clip.width, clip.depth, clip.height].filter(Boolean).join(' · ') || '—';

/**
 * SS-1 — what a failed save says. The server's own words when it sends any;
 * the status when it does not, so "Could not save" never stands for a note
 * that in fact landed.
 */
const noteTrouble = (error, what) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  const status = error?.response?.status;
  if (status) return `The server refused ${what} (${status}) — reload to see what it holds`;
  return `Could not reach the server with ${what}`;
};

// PR-2 — what each preset shows. Office is everything but the internal note.
export const PDF_PRESETS = {
  office: { price: true, vendor: true, approval: true, trade_note: true, internal: false },
  trade: { price: false, vendor: false, approval: false, trade_note: true, internal: false },
  client: { price: true, vendor: false, approval: true, trade_note: false, internal: false },
};

export default function ClipDetail() {
  const { clipId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [clip, setClip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [approving, setApproving] = useState(false);
  const [linkedSelections, setLinkedSelections] = useState([]);
  // CS-1a — the facts that live outside the clip: plan, estimate, order, tag.
  const [context, setContext] = useState(null);
  const [internalNote, setInternalNote] = useState('');
  const [tradeNote, setTradeNote] = useState('');
  // CL-1 — the Spec Category is fixable here, and the SpecNum follows it.
  const [specCategories, setSpecCategories] = useState([]);
  const [recategorising, setRecategorising] = useState(false);
  // CL-2 — Print label opens the Material Tag pre-print step for this clip:
  // the tag it already has, or a new one prefilled from the clip.
  const [tagCreate, setTagCreate] = useState(false);
  const [reprintTag, setReprintTag] = useState(null);
  const [tagBusy, setTagBusy] = useState(false);
  // PR-1 — the receipt: count, where it is, its spot, the state it arrived in.
  const [receiving, setReceiving] = useState(false);
  // PR-1b — Edit opens the sheet on the last receipt's own values.
  const [fixReceipt, setFixReceipt] = useState(null);
  const [tagPanel, setTagPanel] = useState(false);
  // SS-1 — the picture on show. Looking is not saving: the hero says which one
  // would become the primary, and only that button writes it.
  const [shown, setShown] = useState(null);
  // PR-2 — what goes on the printed sheet. The preset is remembered per browser.
  const [pdfPanel, setPdfPanel] = useState(false);
  const [pdfPreset, setPdfPreset] = useState(
    () => localStorage.getItem('km-spec-pdf-preset') || 'office');
  const [pdfFlags, setPdfFlags] = useState(PDF_PRESETS.office);
  const [pdfBusy, setPdfBusy] = useState(false);
  
  // Calculator states
  const [sqftNeeded, setSqftNeeded] = useState('');
  const [wasteFactor, setWasteFactor] = useState('10');
  const [calculatedCases, setCalculatedCases] = useState(null);

  // "Back to Library" — return to the EXACT view the user came from (project
  // hub Clips tab, project clips library, or global library), not '/'.
  // Priority:
  //   1) Browser history (navigate -1) when we have a real history entry —
  //      preserves scroll position naturally via React Router's history stack.
  //   2) Explicit `state.from` carried over from ClipCard (set in
  //      components/ClipCard.js).
  //   3) `clip.project_id` → land on that project's Clips tab.
  //   4) Last resort: global '/clips' library.
  const handleBack = () => {
    const fromState = location.state?.from;
    const hasHistory = location.key && location.key !== 'default';
    if (hasHistory) {
      // navigate(-1) restores scroll position and any in-memory filters the
      // origin view had. This is the typical case (user clicked a clip card).
      navigate(-1);
      return;
    }
    if (fromState) {
      navigate(fromState);
      return;
    }
    if (clip?.project_id) {
      navigate(`/projects/${clip.project_id}/hub?tab=clips`);
      return;
    }
    navigate('/clips');
  };

  useEffect(() => {
    fetchClip();
    fetchLinkedSelections();
    fetchContext();
  }, [clipId]);

  const fetchContext = async () => {
    try {
      const response = await axios.get(`${API}/clips/${clipId}/context`);
      setContext(response.data);
      setInternalNote(response.data.internal_note || '');
      setTradeNote(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching clip context:', error);
    }
  };

  // SS-1 — the picture on show, and the one action that saves it.
  const heroImage = shown || clip?.primary_image || (clip?.images || [])[0] || '';
  const [primaryBusy, setPrimaryBusy] = useState(false);
  const makePrimary = async () => {
    if (!heroImage) return;
    try {
      setPrimaryBusy(true);
      await axios.put(`${API}/clips/${clipId}`, { primary_image: heroImage });
      setClip((prev) => ({ ...(prev || {}), primary_image: heroImage }));
      toast.success('Primary picture saved');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'Could not save that picture');
    } finally { setPrimaryBusy(false); }
  };

  // PS-3g4 — add and remove pictures; removing the primary hands it to the first remaining.
  const [picBusy, setPicBusy] = useState(false);
  // PS-3g4 — the × is two-tap: armed for 3 s, then back to ×.
  const [confirmRemove, setConfirmRemove] = useState('');
  useEffect(() => {
    if (!confirmRemove) return undefined;
    const t = setTimeout(() => setConfirmRemove(''), 3000);
    return () => clearTimeout(t);
  }, [confirmRemove]);
  const savePictures = async (images, primary) => {
    const primary_image = images.includes(primary) ? primary : (images[0] || '');
    await axios.put(`${API}/clips/${clipId}`, { images, primary_image });
    setClip((prev) => ({ ...(prev || {}), images, primary_image }));
    if (!images.includes(shown)) setShown('');
  };
  const addPictures = async (files) => {
    const list = Array.from(files || []); if (!list.length) return;
    setPicBusy(true);
    try {
      const urls = [];
      for (const f of list) {
        const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
        const { data } = await axios.post(`${API}/upload/base64`, { base64_data: dataUrl, filename: `clip_${clipId}_${Date.now()}_${urls.length}.jpg` });
        if (data?.url) urls.push(data.url);
      }
      const images = [...(clip.images || []), ...urls];
      await savePictures(images, clip.primary_image || images[0]);
      toast.success(urls.length === 1 ? 'Picture added' : `${urls.length} pictures added`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add the picture'); }
    finally { setPicBusy(false); }
  };
  const removePicture = async (img) => {
    setPicBusy(true);
    try { await savePictures((clip.images || []).filter((u) => u !== img), clip.primary_image); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove the picture'); }
    finally { setPicBusy(false); }
  };

  // SS-1 — a clip added from the Clipper while this page sat open: read it
  // again when the window comes back, so the first look is the current one.
  useEffect(() => {
    const again = () => {
      if (document.visibilityState !== 'visible') return;
      fetchClip();
      fetchContext();
    };
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [clipId]);

  // CS-1b — the trade note, saved the same way on blur.
  const saveTradeNote = async () => {
    if ((context?.notes || '') === tradeNote) return;
    try {
      await axios.put(`${API}/clips/${clipId}`, { notes: tradeNote });
      setContext(prev => ({ ...(prev || {}), notes: tradeNote }));
      setClip(prev => ({ ...prev, notes: tradeNote }));
      toast.success('Note saved');
    } catch (error) {
      console.error('Error saving the note:', error);
      toast.error(noteTrouble(error, 'that note'));
    }
  };

  // RM-1 — a room is an id and a name, written together.
  const saveRoom = async (room) => {
    if ((clip?.room_id || '') === (room.id || '') && (clip?.room || '') === (room.name || '')) return;
    try {
      await axios.put(`${API}/clips/${clipId}`, { room: room.name || '', room_id: room.id || null });
      setClip(prev => ({ ...(prev || {}), room: room.name || '', room_id: room.id || null }));
      setEditData(prev => ({ ...(prev || {}), room: room.name || '', room_id: room.id || null }));
      toast.success('Room saved');
    } catch (error) {
      console.error('Error saving the room:', error);
      toast.error('Could not save that');
    }
  };

  // CS-2 — Room and Location edited on the sheet itself, saved through the
  // same PUT the Edit form uses, with the notes' own toast.
  const saveWhere = async (field, value) => {
    if ((clip?.[field] || '') === (value || '')) return;
    try {
      await axios.put(`${API}/clips/${clipId}`, { [field]: value });
      setClip(prev => ({ ...(prev || {}), [field]: value }));
      setEditData(prev => ({ ...(prev || {}), [field]: value }));
      toast.success(field === 'room' ? 'Room saved'
        : field === 'tag_name' ? 'Tag name saved' : 'Location saved');
    } catch (error) {
      console.error('Error saving where it goes:', error);
      toast.error('Could not save that');
    }
  };

  // CS-2 — the sheet prints on paper colours whichever skin is on screen.
  // PR-2 — this page is no longer printed by the browser: the sheet is a PDF
  // drawn on the server, so the print skin and the PR-1 opt-in are gone.

  // CS-2 — one share per clip: the options can change, the link does not.
  // SS-1 — and the link opens the app it was made in. The server writes its
  // canonical host (FRONTEND_URL); a token minted here only exists here, so
  // the copied address keeps this origin and the token from the server.
  const [share, setShare] = useState(null);
  const [sharePanel, setSharePanel] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareOptions, setShareOptions] = useState({
    include_price: true, include_attachments: true, include_plan: false,
  });

  useEffect(() => {
    axios.get(`${API}/clips/${clipId}/share`)
      .then(({ data }) => {
        setShare(data.token ? data : null);
        setShareOptions({
          include_price: data.include_price, include_attachments: data.include_attachments,
          include_plan: data.include_plan,
        });
      })
      .catch(() => setShare(null));
  }, [clipId]);

  const saveShare = async (next) => {
    setShareBusy(true);
    try {
      const { data } = await axios.post(`${API}/clips/${clipId}/share`, next);
      setShare(data);
      setShareOptions({
        include_price: data.include_price, include_attachments: data.include_attachments,
        include_plan: data.include_plan,
      });
      toast.success('Link ready');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not make that link');
    } finally {
      setShareBusy(false);
    }
  };

  const shareLink = share?.token
    ? `${window.location.origin}/s/${share.token}`
    : (share?.url || '');

  // PR-2 — the sheet as a document: the route draws it, the viewer prints it.
  const makeSheetPdf = async () => {
    try {
      setPdfBusy(true);
      const answer = await axios.get(`${API}/clips/${clipId}/print.pdf`, {
        params: { preset: pdfPreset, ...Object.fromEntries(
          Object.entries(pdfFlags).map(([key, on]) => [key, on ? 1 : 0])) },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([answer.data], { type: 'application/pdf' }));
      if (!window.open(url, '_blank')) toast.error('Allow pop-ups to open the sheet');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That sheet did not print');
    } finally { setPdfBusy(false); }
  };

  const choosePreset = (preset) => {
    setPdfPreset(preset);
    setPdfFlags(PDF_PRESETS[preset]);
    localStorage.setItem('km-spec-pdf-preset', preset);
  };

  const toggleShareOption = (field) => {
    const next = { ...shareOptions, [field]: !shareOptions[field] };
    setShareOptions(next);
    if (share) saveShare(next);
  };

  const stopSharing = async () => {
    setShareBusy(true);
    try {
      await axios.delete(`${API}/clips/${clipId}/share`);
      setShare(null);
      toast.success('Stopped sharing');
    } catch (error) {
      toast.error('Could not stop that share');
    } finally {
      setShareBusy(false);
    }
  };

  // Saved on blur through the update the page already uses.
  const saveInternalNote = async () => {
    if ((context?.internal_note || '') === internalNote) return;
    try {
      await axios.put(`${API}/clips/${clipId}`, { internal_note: internalNote });
      setContext(prev => ({ ...(prev || {}), internal_note: internalNote }));
      toast.success('Internal note saved');
    } catch (error) {
      console.error('Error saving the internal note:', error);
      toast.error(noteTrouble(error, 'that internal note'));
    }
  };

  useEffect(() => {
    axios.get(`${API}/spec-categories`)
      .then(res => setSpecCategories(Array.isArray(res.data) ? res.data : res.data.categories || []))
      .catch(err => console.error('Error loading spec categories:', err));
  }, []);

  // CL-1 — one path: the assign-specnum route retires the old number through
  // the ledger and mints the new one.
  const handleSpecCategoryChange = async (prefix) => {
    setRecategorising(true);
    try {
      const res = await axios.post(`${API}/clips/${clipId}/assign-specnum`, { spec_category: prefix });
      setClip(prev => ({ ...prev, spec_category: prefix, spec_num: res.data.spec_num }));
      toast.success(`SpecNum ${res.data.spec_num}`);
    } catch (error) {
      console.error('Error changing spec category:', error);
      toast.error(error.response?.data?.detail || 'Could not change the Spec Category');
    } finally {
      setRecategorising(false);
    }
  };

  const fetchClip = async () => {
    try {
      const response = await axios.get(`${API}/clips/${clipId}`);
      setClip(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching clip:', error);
      toast.error('Failed to load clip');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedSelections = async () => {
    try {
      const response = await axios.get(`${API}/clips/${clipId}/linked-selections`);
      setLinkedSelections(response.data.linked_selections || []);
    } catch (error) {
      console.error('Error fetching linked selections:', error);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/clips/${clipId}`, editData);
      toast.success('Clip updated successfully');
      setEditing(false);
      fetchClip();
    } catch (error) {
      console.error('Error updating clip:', error);
      toast.error('Failed to update clip');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clips/${clipId}`);
      toast.success('Clip deleted successfully');
      // CL-3 — back to where the spec sheet lived, not to the dashboard.
      navigate(clip?.project_id ? `/projects/${clip.project_id}/hub?tab=clips` : '/clips');
    } catch (error) {
      console.error('Error deleting clip:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete clip');
    }
  };

  const calculateCases = () => {
    if (!sqftNeeded || !clip.coverage_area) {
      toast.error('Please enter square footage needed and ensure coverage area is set');
      return;
    }

    const sqft = parseFloat(sqftNeeded);
    const waste = parseFloat(wasteFactor) / 100;
    const coverage = parseFloat(clip.coverage_area);

    if (isNaN(sqft) || isNaN(waste) || isNaN(coverage)) {
      toast.error('Invalid numbers entered');
      return;
    }

    const totalWithWaste = sqft * (1 + waste);
    const casesNeeded = Math.ceil(totalWithWaste / coverage);
    const totalCoverage = casesNeeded * coverage;
    const actualWaste = ((totalCoverage - sqft) / sqft * 100).toFixed(1);

    setCalculatedCases({
      cases: casesNeeded,
      totalCoverage: totalCoverage.toFixed(2),
      actualWaste: actualWaste
    });
  };

  // CL-2 — one tag per clip. If this clip already has a Material Tag (by
  // clip_id, or by the SpecNum it carries on this project), that tag's fields
  // open for correction and it reprints; otherwise the tag form opens
  // prefilled from the clip and mints one on print.
  const openTagStep = async () => {
    setTagBusy(true);
    try {
      const mine = await axios.get(`${API}/projects/${clip.project_id}/material-tags`,
        { params: { clip_id: clipId } });
      let existing = (Array.isArray(mine.data) ? mine.data : [])
        .find(t => (t.kind || '') !== 'spot');
      if (!existing && clip.spec_num) {
        const all = await axios.get(`${API}/projects/${clip.project_id}/material-tags`);
        existing = (Array.isArray(all.data) ? all.data : [])
          .find(t => t.spec_num === clip.spec_num && (t.kind || '') !== 'spot');
      }
      // MT-2 — one form: a clip that already has a tag reprints the same code.
      setReprintTag(existing || null);
      setTagCreate(true);
    } catch (error) {
      console.error('Error opening the tag step:', error);
      toast.error(error.response?.data?.detail || 'Could not open the tag');
    } finally {
      setTagBusy(false);
    }
  };

  const handleApprove = async (status) => {
    if (!clip.project_id) {
      toast.error('Clip must be assigned to a project before approval');
      return;
    }
    
    setApproving(true);
    try {
      const response = await axios.post(`${API}/clips/${clipId}/approve`, {
        status,
        approved_by: 'Designer'
      });
      
      if (status === 'approved') {
        // CL-2a — a number is only named when there is one.
        const minted = response.data.spec_num || clip.spec_num;
        toast.success(`Clip approved${minted ? ` · ${minted}` : ''}`);
      } else if (status === 'rejected') {
        toast.success('Clip rejected');
      } else {
        toast.success('Clip status reset to pending');
      }
      
      fetchClip();
    } catch (error) {
      console.error('Error approving clip:', error);
      toast.error(error.response?.data?.detail || 'Failed to update approval status');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!clip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Clip not found</p>
      </div>
    );
  }


  const statusChip = clip.client_approval_status === 'approved' ? (
    <Badge className="bg-green-500 text-white flex items-center gap-1.5" data-testid="clip-approval-chip">
      <CheckCircle2 className="w-4 h-4" />Approved
    </Badge>
  ) : clip.client_approval_status === 'rejected' ? (
    <Badge className="bg-red-500 text-white flex items-center gap-1.5" data-testid="clip-approval-chip">
      <XCircle className="w-4 h-4" />Rejected
    </Badge>
  ) : (
    <Badge variant="secondary" className="flex items-center gap-1.5" data-testid="clip-approval-chip">
      <Clock className="w-4 h-4" />Pending
    </Badge>
  );

  const dimensions = clipDimensions(clip);
  const per = [clip.unit_of_measure, clip.coverage_area && `${clip.coverage_area} per case`,
    clip.shipping_cost && `ship ${clip.shipping_cost}`].filter(Boolean).join(' · ') || '—';
  const specRows = (value) => {
    if (!value) return [];
    if (typeof value === 'string') return [['', value]];
    return Object.entries(value).filter(([, v]) => v !== null && v !== '' && v !== undefined);
  };
  const electrical = specRows(clip.electrical_specs);
  const roughIn = [...specRows(clip.plumbing_specs), ...specRows(clip.gas_specs)];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
        <div className="ksp">
          <div className="ksp__bar" data-km-noprint>
            <Button variant="ghost" onClick={handleBack} data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {clip.project_id ? 'Project Specifications' : 'Clip Library'}
            </Button>
            <span className="sp" />
            <span className="ksp__acts flex items-center gap-2 flex-wrap">
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-button">
                    <X className="w-4 h-4 mr-2" />Cancel
                  </Button>
                  <Button onClick={handleSave} data-testid="save-button">
                    <Save className="w-4 h-4 mr-2" />Save Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-button">
                    <Edit2 className="w-4 h-4 mr-2" />Edit
                  </Button>
                  <Button variant="outline" onClick={() => setPdfPanel((v) => !v)}
                    data-testid="print-sheet-btn">
                    <Printer className="w-4 h-4 mr-2" />Print / Save PDF
                  </Button>
                  <Button variant="outline" onClick={() => setSharePanel((v) => !v)}
                    data-testid="share-sheet-btn">Share…</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" data-testid="delete-button">
                        <Trash2 className="w-4 h-4 mr-2" />Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This clip will be permanently deleted.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-button">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </span>
          </div>

            {pdfPanel && (
              /* PR-2 — what goes on the printed sheet, in the Share menu's own style. */
              <div className="ksp__share" data-testid="pdf-panel">
                <div className="ksp__share__opts">
                  {['office', 'trade', 'client'].map((preset) => (
                    <button type="button" key={preset}
                      className={`ksp__share__opt${pdfPreset === preset ? ' on' : ''}`}
                      onClick={() => choosePreset(preset)}
                      data-testid={`pdf-preset-${preset}`}>
                      {preset[0].toUpperCase() + preset.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="ksp__share__opts">
                  {[['price', 'Price'], ['vendor', 'Vendor and link'],
                    ['approval', 'Approval & order'], ['trade_note', 'Trade note'],
                    ['internal', 'Internal note']].map(([field, label]) => (
                    <label className="ksp__share__opt" key={field}>
                      <input type="checkbox" checked={!!pdfFlags[field]}
                        onChange={() => setPdfFlags((f) => ({ ...f, [field]: !f[field] }))}
                        data-testid={`pdf-opt-${field}`} />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="ksp__share__link">
                  <span className="kso-hint" style={{ margin: 0 }}>
                    The internal note is never on a shared copy.
                  </span>
                  <Button variant="outline" size="sm" onClick={makeSheetPdf}
                    disabled={pdfBusy} data-testid="pdf-make">
                    {pdfBusy ? 'Making…' : 'Make PDF'}
                  </Button>
                </div>
              </div>
            )}


          {/* CS-2 — the share panel, under the bar, off the page when printing. */}
          {sharePanel && !editing && (
            <div className="ksp__share" data-km-noprint data-testid="share-panel">
              <div className="ksp__share__opts">
                {[['include_price', 'Price'], ['include_attachments', 'Attachments'],
                  ['include_plan', 'On the plan']].map(([field, label]) => (
                  <label className="ksp__share__opt" key={field}>
                    <input type="checkbox" checked={shareOptions[field]}
                      onChange={() => toggleShareOption(field)}
                      data-testid={`share-opt-${field}`} />
                    {label}
                  </label>
                ))}
              </div>
              {share ? (
                <div className="ksp__share__link">
                  <code data-testid="share-link">{shareLink}</code>
                  <Button variant="outline" size="sm" data-testid="share-copy"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(shareLink);
                        setShareCopied(true);
                        setTimeout(() => setShareCopied(false), 1500);
                      } catch (_e) { toast.error('Copy that link by hand'); }
                    }}>
                    {shareCopied ? 'Copied' : 'Copy link'}
                  </Button>
                  <Button variant="outline" size="sm" disabled={shareBusy}
                    onClick={stopSharing} data-testid="share-stop">Stop sharing</Button>
                </div>
              ) : (
                <Button size="sm" disabled={shareBusy} onClick={() => saveShare(shareOptions)}
                  data-testid="share-create">Create link</Button>
              )}
              <div className="ksp__share__hint">
                Anyone with the link sees this sheet. Your internal note is never on it.
              </div>
            </div>
          )}

          <div className="ksp__body" style={editing ? { gridTemplateColumns: 'minmax(0,1fr)' } : undefined}>
            {editing ? (
              <div data-testid="clip-details">

              <>
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Basic Information</h3>
                  <div className="space-y-2">
                    <Label htmlFor="title">Title / Work Description</Label>
                    <Input
                      id="title"
                      data-testid="edit-title-input"
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="spec-num">Spec Number</Label>
                      <Input
                        id="spec-num"
                        value={editData.spec_num || ''}
                        onChange={(e) => setEditData({ ...editData, spec_num: e.target.value })}
                        placeholder="e.g., ELE-001"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="product-type">Product Type</Label>
                      <Input
                        id="product-type"
                        data-testid="edit-type-input"
                        value={editData.product_type || ''}
                        onChange={(e) => setEditData({ ...editData, product_type: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="model-number">Product Name / Model #</Label>
                      <Input
                        id="model-number"
                        value={editData.model_number || ''}
                        onChange={(e) => setEditData({ ...editData, model_number: e.target.value })}
                        placeholder="e.g., K-5871-5UA3"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manufacturer">Brand / Manufacturer</Label>
                      <Input
                        id="manufacturer"
                        data-testid="edit-manufacturer-input"
                        value={editData.manufacturer || ''}
                        onChange={(e) => setEditData({ ...editData, manufacturer: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="finish">Finish / Color</Label>
                      <Input
                        id="finish"
                        value={editData.finish || ''}
                        onChange={(e) => setEditData({ ...editData, finish: e.target.value })}
                        placeholder="e.g., Brushed Nickel"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dimensions">Dimensions</Label>
                      <Input
                        id="dimensions"
                        value={editData.dimensions || ''}
                        onChange={(e) => setEditData({ ...editData, dimensions: e.target.value })}
                        placeholder='e.g., 24" x 36"'
                      />
                    </div>
                  </div>
                </div>
                
                {/* Links & Documents */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Links & Documents</h3>
                  <div className="space-y-2">
                    <Label htmlFor="product-url" className="flex items-center gap-2">
                      <Link className="w-4 h-4" />
                      Product Link
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="product-url"
                        value={editData.url || ''}
                        onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                        placeholder="https://..."
                        className="flex-1"
                      />
                      {editData.url && (
                        <Button type="button" variant="outline" size="icon" onClick={() => window.open(editData.url, '_blank')}>
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pdf-url" className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Manufacturer PDF / Spec Sheet
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="pdf-url"
                        value={editData.pdf_url || ''}
                        onChange={(e) => setEditData({ ...editData, pdf_url: e.target.value })}
                        placeholder="https://... (link to PDF)"
                        className="flex-1"
                      />
                      {editData.pdf_url && (
                        <Button type="button" variant="outline" size="icon" onClick={() => window.open(editData.pdf_url, '_blank')}>
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="install-url">Installation Instructions URL</Label>
                    <div className="flex gap-2">
                      <Input
                        id="install-url"
                        value={editData.install_instructions_url || ''}
                        onChange={(e) => setEditData({ ...editData, install_instructions_url: e.target.value })}
                        placeholder="https://..."
                        className="flex-1"
                      />
                      {editData.install_instructions_url && (
                        <Button type="button" variant="outline" size="icon" onClick={() => window.open(editData.install_instructions_url, '_blank')}>
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Pricing & Quantity</h3>
                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    data-testid="edit-price-input"
                    value={editData.price || ''}
                    onChange={(e) => setEditData({ ...editData, price: e.target.value })}
                    placeholder="e.g., $45.99 per case"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="unit-price">Unit Price</Label>
                    <Input
                      id="unit-price"
                      value={editData.unit_price || ''}
                      onChange={(e) => setEditData({ ...editData, unit_price: e.target.value })}
                      placeholder="e.g., $2.50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit-measure">Unit of Measure</Label>
                    <Input
                      id="unit-measure"
                      value={editData.unit_of_measure || ''}
                      onChange={(e) => setEditData({ ...editData, unit_of_measure: e.target.value })}
                      placeholder="e.g., square foot"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="case-quantity">Case/Box Quantity</Label>
                    <Input
                      id="case-quantity"
                      value={editData.case_quantity || ''}
                      onChange={(e) => setEditData({ ...editData, case_quantity: e.target.value })}
                      placeholder="e.g., 12 tiles per box"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coverage-area">Coverage per Case</Label>
                    <Input
                      id="coverage-area"
                      value={editData.coverage_area || ''}
                      onChange={(e) => setEditData({ ...editData, coverage_area: e.target.value })}
                      placeholder="e.g., 10.5 (sq ft)"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="minimum-order">Minimum Order</Label>
                  <Input
                    id="minimum-order"
                    value={editData.minimum_order || ''}
                    onChange={(e) => setEditData({ ...editData, minimum_order: e.target.value })}
                    placeholder="e.g., 1 case minimum"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lead-time">Lead Time</Label>
                  <Input
                    id="lead-time"
                    value={editData.lead_time || ''}
                    onChange={(e) => setEditData({ ...editData, lead_time: e.target.value })}
                    placeholder="e.g., 3-4 weeks"
                  />
                </div>
                </div>
                
                {/* Location */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Location</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="room">Room</Label>
                    <RoomPicker projectId={clip?.project_id || null}
                      value={{ id: editData.room_id || null, name: editData.room || '' }}
                      onChange={(r) => setEditData({ ...editData, room: r.name, room_id: r.id || null })}
                      testid="edit-room" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="room-location">Location in Room</Label>
                    <Input
                      id="room-location"
                      value={editData.room_location || ''}
                      onChange={(e) => setEditData({ ...editData, room_location: e.target.value })}
                      placeholder="e.g., Shower wall, Island countertop"
                    />
                  </div>
                </div>
                </div>
                
                {/* Notes */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Notes & Details</h3>
                  <div className="space-y-2">
                    <Label htmlFor="features">Features / Description</Label>
                    <Textarea
                      id="features"
                      value={editData.features || ''}
                      onChange={(e) => setEditData({ ...editData, features: e.target.value })}
                      rows={2}
                      placeholder="Product features and specifications..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (Client Visible)</Label>
                    <Textarea
                      id="notes"
                      data-testid="edit-notes-input"
                      value={editData.notes || ''}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="internal-notes">Internal Notes (Not visible to client)</Label>
                    <Textarea
                      id="internal-notes"
                      value={editData.internal_notes || ''}
                      onChange={(e) => setEditData({ ...editData, internal_notes: e.target.value })}
                      rows={3}
                      placeholder="Private notes for internal use..."
                    />
                  </div>
                </div>
              </>

              </div>
            ) : (
              <>
                <div className="ksp__plate" data-testid="clip-images">
                  <div className="ksp__hero">
                    {clip.images && clip.images.length > 0 ? (
                      <>
                        <img src={heroImage} alt={clip.title} data-testid="clip-hero-image" />
                        {heroImage !== (clip.primary_image || clip.images[0]) ? (
                          <button type="button" className="ksp__hero__set" data-km-noprint
                            disabled={primaryBusy} onClick={makePrimary}
                            data-testid="clip-make-primary">
                            {primaryBusy ? 'Saving…' : 'Make primary'}
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <p className="text-muted-foreground">No images available</p>
                    )}
                  </div>
                  <div className="ksp__thumbs" data-km-noprint data-testid="clip-thumbs" onClick={() => setConfirmRemove('')}>
                    {(clip.images || []).map((img, idx) => (
                      <span key={idx} className={`img${img === heroImage ? ' on' : ''}`}>
                        <button type="button" className="img__pick" onClick={() => setShown(img)} data-testid={`clip-thumb-${idx}`}>
                          <img src={img} alt={`${clip.title} ${idx + 1}`} />
                        </button>
                        {img === (clip.primary_image || clip.images[0]) ? <i className="img__star" aria-label="Primary">★</i> : null}
                        <button type="button" className={`img__x${confirmRemove === img ? ' is-armed' : ''}`}
                          aria-label={confirmRemove === img ? 'Tap again to remove' : 'Remove picture'} disabled={picBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirmRemove === img) { setConfirmRemove(''); removePicture(img); } else setConfirmRemove(img);
                          }} data-testid={`clip-thumb-remove-${idx}`}>{confirmRemove === img ? 'Remove?' : '×'}</button>
                      </span>
                    ))}
                    <label className="img img__add" data-testid="clip-add-picture">
                      <input type="file" accept="image/*" multiple hidden disabled={picBusy} onChange={(e) => addPictures(e.target.files)} />
                      <span>{picBusy ? '…' : '+'}</span><small>Add</small>
                    </label>
                  </div>
                  <div className="ksp__files" data-testid="clip-pdfs">
                    <div className="hd">Spec sheets · {(clip.pdf_files || []).length}</div>
                    {(clip.pdf_files || []).length === 0 ? (
                      <div className="f"><span>Nothing attached yet</span><span /><span /></div>
                    ) : clip.pdf_files.map((pdf, idx) => (
                      <div className="f" key={idx}>
                        <span className="truncate">{pdf.name || `Specification ${idx + 1}.pdf`}</span>
                        <span className="k">{pdf.pages ? `${pdf.pages} pp` : (pdf.size || '')}</span>
                        <a href={pdf.url} target="_blank" rel="noopener noreferrer"
                          className="kso-btn" data-testid={`clip-pdf-open-${idx}`}>Open</a>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="ksp__main" data-testid="clip-details">
                  <div className="ksp__id">
                    {clip.spec_num ? (
                      <Badge className="bg-[#70170F] text-white font-mono px-3 py-1" data-testid="clip-specnum-badge">
                        {clip.spec_num}
                      </Badge>
                    ) : (
                      <Badge variant="outline" data-testid="clip-specnum-badge">no number yet</Badge>
                    )}
                    <select
                      value={clip.spec_category || ''}
                      onChange={(e) => handleSpecCategoryChange(e.target.value)}
                      disabled={recategorising || !clip.project_id}
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground disabled:opacity-50"
                      data-km-noprint
                      data-testid="clip-spec-category-select"
                    >
                      <option value="">Uncategorized</option>
                      {specCategories.map(cat => (
                        <option key={cat.prefix || cat.name} value={cat.prefix}>
                          {cat.prefix} — {cat.name}
                        </option>
                      ))}
                    </select>
                    {statusChip}
                    <ClipStar clip={clip} currentUserId={getCurrentUserId()}
                      onChange={(list) => setClip(prev => ({ ...prev, starred_by: list }))}
                      testIdSuffix="-header" />
                    <HelpIcon elementId="clip-card" position="inline" />
                    {context?.clipped?.at && (
                      <span className="meta" data-testid="clip-clipped-meta">
                        Clipped {new Date(context.clipped.at).toLocaleDateString()}
                        {context.clipped.by_name ? ` by ${context.clipped.by_name}` : ''}
                        {` · ${context.clipped.source}`}
                      </span>
                    )}
                  </div>

                  <h1 className="ksp__title" data-testid="clip-title">{clip.title}</h1>

                  {/* PR-2c — the name the trade uses, on the sheet page itself.
                      The same field the Material Tag form writes, so the two
                      never disagree; on paper it prints above the title. */}
                  <div className="ksp__tagname">
                    <span className="k">Tag name</span>
                    <input className="ksp__field" defaultValue={clip.tag_name || ''}
                      placeholder="What the trade calls it — e.g. Dishwasher"
                      onBlur={(e) => saveWhere('tag_name', e.target.value)}
                      data-testid="clip-tag-name" />
                  </div>

                  <div className="ksp__sub">
                    {clip.manufacturer && <span data-testid="clip-manufacturer"><b>{clip.manufacturer}</b></span>}
                    {(clip.model || clip.model_number) && <span>Model {clip.model || clip.model_number}</span>}
                    {clip.finish && <span>Finish {clip.finish}</span>}
                    {clip.product_type && <span data-testid="clip-product-type">{clip.product_type}</span>}
                    {clip.url && (
                      <a href={clip.url} target="_blank" rel="noopener noreferrer" data-testid="clip-url">
                        View original
                      </a>
                    )}
                    {(clip.tags || []).map(tag => (
                      <Badge key={tag} variant="secondary" data-testid="clip-tags">{tag}</Badge>
                    ))}
                  </div>

                  <div className="ksp__facts">
                    <div>
                      <span className="k">Price</span>
                      <span className="v big" data-testid="clip-price">{clip.price || clip.unit_price || '—'}</span>
                    </div>
                    <div>
                      <span className="k">Dimensions W · D · H</span>
                      <span className="v">{dimensions}</span>
                    </div>
                    <div>
                      <span className="k">Lead time</span>
                      <span className="v">{clip.lead_time || clip.availability || '—'}</span>
                    </div>
                    <div>
                      <span className="k">Per</span>
                      <span className="v">{per}</span>
                    </div>
                  </div>

                  <div className="ksp__where">
                    <div className="ksp__card">
                      <div className="hd">Where it goes</div>
                      <div className="bd">
                        <div className="row">
                          <span className="k">Project</span>
                          <span>{clip.project_name || (clip.project_id ? 'On a project' : 'Not on a project')}</span>
                        </div>
                        <div className="row">
                          <span className="k">Room</span>
                          <span>
                            <RoomPicker projectId={clip?.project_id || null}
                              value={{ id: clip.room_id || null, name: clip.room || '' }}
                              onChange={(r) => saveRoom({ id: r.id || null, name: r.name || '' })}
                              testid="clip-room" />
                          </span>
                        </div>
                        <div className="row">
                          <span className="k">Location</span>
                          <span>
                            <input className="ksp__field" defaultValue={clip.room_location || ''}
                              placeholder="e.g. Shower wall"
                              onBlur={(e) => saveWhere('room_location', e.target.value)}
                              data-testid="clip-location" />
                          </span>
                        </div>
                        {clip.project_id && context?.plan && (
                          <div className="row">
                            <span className="k">On the plan</span>
                            <span data-testid="clip-on-plan">
                              {context.plan.points > 0 ? (
                                <>
                                  {context.plan.points} point{context.plan.points === 1 ? '' : 's'}
                                  {' · '}
                                  <a className="underline"
                                    href={`/projects/${clip.project_id}/floor-plans?plan=${context.plan.plan_id}&page=${context.plan.page_number}`}
                                    data-testid="clip-show-on-plan">Show on plan</a>
                                </>
                              ) : 'not placed yet'}
                            </span>
                          </div>
                        )}
                        <div className="row">
                          <span className="k">Estimate</span>
                          <span data-testid="clip-estimate-line">
                            {/* SS-1 — the tick is always here, so the line can
                                be taken off the estimate on the sheet itself;
                                the sheet then rereads the clip, and the SpecNum
                                goes with the line. */}
                            <EstimateTick clip={clip}
                              estimateItem={clip.estimate_item}
                              onUpdate={async () => { await fetchClip(); await fetchContext(); }} />
                            {context?.estimate || clip.estimate_item ? (
                              <span className="meta" data-testid="clip-estimate-detail">
                                {`line · qty ${clip.estimate_item?.quantity
                                  ?? context?.estimate?.quantity ?? '—'}`
                                  + `${clip.estimate_item?.unit ? ` ${clip.estimate_item.unit}` : ''}`}
                                {context?.estimate?.allowance_amount != null
                                  ? ` · allowance $${context.estimate.allowance_amount}` : ''}
                                {context?.estimate?.variance != null
                                  ? ` · ${context.estimate.variance > 0 ? 'over' : 'under'} $${Math.abs(context.estimate.variance)}` : ''}
                              </span>
                            ) : null}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="ksp__card ksp__appr">
                      <div className="hd">Approval</div>
                      <div className="bd">
                        <div className="row">
                          <span className="k">Status</span>
                          <span>
                            {statusChip}
                            {clip.client_approval_date
                              && ` ${new Date(clip.client_approval_date).toLocaleDateString()}`}
                            {clip.client_approved_by ? ` · ${clip.client_approved_by}` : ''}
                          </span>
                        </div>
                        {clip.client_approval_notes && (
                          <div className="row">
                            <span className="k">Note</span>
                            <span>"{clip.client_approval_notes}"</span>
                          </div>
                        )}
                        {clip.project_id ? (
                          <div className="acts">
                            {clip.client_approval_status !== 'approved' && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApprove('approved')} disabled={approving}
                                data-testid="approve-clip-btn">
                                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                {approving ? 'Processing...' : 'Approve & Assign SpecNum'}
                              </Button>
                            )}
                            {clip.client_approval_status !== 'rejected' && (
                              <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                                onClick={() => handleApprove('rejected')} disabled={approving}
                                data-testid="reject-clip-btn">
                                <XCircle className="w-4 h-4 mr-1.5" />Reject
                              </Button>
                            )}
                            {clip.client_approval_status !== 'pending' && (
                              <Button size="sm" variant="ghost" onClick={() => handleApprove('pending')}
                                disabled={approving} data-testid="reset-clip-btn">
                                Reset to Pending
                              </Button>
                            )}
                            {/* CL-3 — a clip that is on the estimate, or on a
                                signed project's change order, can be labelled. */}
                            {((clip.spec_num && !clip.spec_num.startsWith('XXX-'))
                              || clip.change_order_number) && (
                              <Button size="sm" variant="outline" onClick={openTagStep} disabled={tagBusy}
                                data-testid="print-label-btn">
                                <Printer className="w-4 h-4 mr-1.5" />Print label
                              </Button>
                            )}
                          </div>
                        ) : (
                          <p className="text-muted-foreground">
                            Assign this clip to a project to enable approval workflow.
                          </p>
                        )}
                        {/* PR-1 — the order strip: what was ordered, what has
                            arrived, and what has been labelled. */}
                        <div className="row kpr-strip">
                          <span className="k">Order</span>
                          <span data-testid="clip-ordered">
                            {context?.order
                              ? `${String(context.order.po_number || '').startsWith('PO') ? '' : 'PO '}${context.order.po_number}${context.order.ordered_date ? ` · ${String(context.order.ordered_date).slice(0, 10)}` : ''}`
                              : 'not ordered'}
                          </span>
                          {context?.order?.po_id && (
                            <button type="button" className="kpr-btn"
                              onClick={() => navigate('/procurement')}
                              data-testid="clip-open-po">Open PO</button>
                          )}
                        </div>
                        <div className="row kpr-strip">
                          <span className="k">Received</span>
                          <span className="kpr-fact">
                          <span data-testid="clip-received">
                            {clip.receiving ? receivedLine(clip.receiving) : '—'}
                          </span>
                          {clip.receiving
                            && ['damaged', 'short'].includes(clip.receiving.state) ? (
                            <span className="kpr-line kpr-line--damaged"
                              data-testid="clip-received-flag">
                              {clip.receiving.state === 'damaged' ? 'damaged' : 'short'}
                            </span>
                          ) : null}
                          {!clip.receiving ? (
                            <button type="button" className="kpr-btn kpr-btn--brand"
                              onClick={() => { setFixReceipt(null); setReceiving(true); }}
                              data-testid="receive-open">Receive…</button>
                          ) : Number(clip.receiving.count) < Number(clip.receiving.of) ? (
                            <button type="button" className="kpr-tagbtn"
                              onClick={() => { setFixReceipt(null); setReceiving(true); }}
                              data-testid="clip-receive-more">Receive more</button>
                          ) : (
                            <button type="button" className="kpr-tagbtn"
                              onClick={() => {
                                const past = clip.receiving.history || [];
                                setFixReceipt(past[past.length - 1] || clip.receiving);
                                setReceiving(true);
                              }}
                              data-testid="clip-receive-edit">Edit</button>
                          )}
                          </span>
                        </div>
                        <div className="row kpr-strip">
                          <span className="k">Tags</span>
                          {/* PR-1d — the mark says how many; no word beside it. */}
                          {clip.tag_count ? (
                            <button type="button" className="km-tag-btn"
                              onClick={() => setTagPanel(true)}
                              title={`${clip.tag_count} printed tag${clip.tag_count === 1 ? '' : 's'}`}
                              data-testid={`clip-tag-mark-${clip.id}`}>
                              <TagMark n={Number(clip.tag_count)} state={clip.tag_state} />
                            </button>
                          ) : null}
                          <span data-testid="clip-tag-line">
                            {clip.tag_count
                              ? ''
                              : context?.tag
                                ? `${context.tag.code} · scanned ${context.tag.scans} time${context.tag.scans === 1 ? '' : 's'}`
                                : 'none yet'}
                          </span>
                          {clip.tag_count ? (
                            <button type="button" className="kpr-btn"
                              onClick={() => setTagPanel(true)}
                              data-testid="clip-open-tags">Open tags</button>
                          ) : (
                            <button type="button" className="kpr-btn"
                              onClick={openTagStep} disabled={tagBusy}
                              data-testid="clip-print-tag">Print tag</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ksp__specs">
                    {electrical.length > 0 && (
                      <div className="ksp__card" data-testid="clip-electrical">
                        <div className="hd">Electrical</div>
                        <div className="bd">
                          {electrical.map(([k, v]) => (
                            <div className="row" key={k || 'e'}>
                              <span className="k">{k.replace(/_/g, ' ') || 'Spec'}</span>
                              <span>{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {roughIn.length > 0 && (
                      <div className="ksp__card" data-testid="clip-rough-in">
                        <div className="hd">Rough-in · plumbing · gas</div>
                        <div className="bd">
                          {roughIn.map(([k, v], i) => (
                            <div className="row" key={`${k}-${i}`}>
                              <span className="k">{k.replace(/_/g, ' ') || 'Spec'}</span>
                              <span>{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="ksp__card ksp__notes" data-km-noprint>
                      <div className="hd">Notes</div>
                      <div className="bd">
                        <span className="k">For the trade</span>
                        <textarea value={tradeNote} onChange={(e) => setTradeNote(e.target.value)}
                          onBlur={saveTradeNote} placeholder="What the crew needs to know"
                          data-testid="clip-notes" />
                        <span className="k">Internal (never shared)</span>
                        <textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)}
                          onBlur={saveInternalNote} placeholder="Only the firm sees this"
                          data-testid="clip-internal-note" />
                      </div>
                    </div>
                  </div>

                  {clip.coverage_area && (
                    <div className="ksp__card" data-testid="clip-calculator">
                      <div className="hd">Case calculator</div>
                      <div className="bd">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="sqft-needed" className="text-xs">
                              Area Needed ({clip.unit_of_measure || 'sq ft'})
                            </Label>
                            <Input id="sqft-needed" type="number" value={sqftNeeded}
                              onChange={(e) => setSqftNeeded(e.target.value)} placeholder="e.g., 250" className="h-10" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="waste-factor" className="text-xs">Waste Factor (%)</Label>
                            <Input id="waste-factor" type="number" value={wasteFactor}
                              onChange={(e) => setWasteFactor(e.target.value)} placeholder="10" className="h-10" />
                          </div>
                        </div>
                        <Button onClick={calculateCases} size="sm">Calculate Cases Needed</Button>
                        {calculatedCases && (
                          <>
                            <div className="row">
                              <span className="k">Cases needed</span>
                              <span data-testid="calculated-cases">{calculatedCases.cases}</span>
                            </div>
                            <div className="row">
                              <span className="k">Total coverage</span>
                              <span>{calculatedCases.totalCoverage} {clip.unit_of_measure || 'sq ft'}</span>
                            </div>
                            <div className="row">
                              <span className="k">Actual waste</span>
                              <span>{calculatedCases.actualWaste}%</span>
                            </div>
                          </>
                        )}
                        {clip.case_quantity && (
                          <div className="row">
                            <span className="k">Case quantity</span>
                            <span>{clip.case_quantity}</span>
                          </div>
                        )}
                        {clip.minimum_order && (
                          <div className="row">
                            <span className="k">Minimum order</span>
                            <span>{clip.minimum_order}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {linkedSelections.length > 0 && (
                    <div className="ksp__card" data-testid="clip-linked-selections">
                      <div className="hd">
                        Linked takeoff selections<span className="n">{linkedSelections.length}</span>
                      </div>
                      <div className="bd">
                        {linkedSelections.map((selection, idx) => (
                          <div className="row" key={selection.measurement_id || idx}
                            onClick={() => {
                              if (selection.project_id && selection.plan_id) {
                                navigate(`/projects/${selection.project_id}/floor-plans?plan=${selection.plan_id}`);
                              }
                            }}
                            style={{ cursor: 'pointer' }}>
                            <span className="k">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {selection.measurement_type === 'count' ? 'Selection' : selection.room_type || 'Location'}
                            </span>
                            <span>
                              {selection.room_name || selection.label || 'Unknown Location'}
                              {selection.plan_name ? ` · ${selection.plan_name}` : ''}
                              {selection.page_number ? ` · page ${selection.page_number}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>


      {/* PR-1 — the Receive sheet, in the dock beside the clip. */}
      {receiving && (
        <ReceiveSheet
          clip={clip}
          poId={context?.order?.po_id}
          poItemId={context?.order?.po_item_id}
          of={Number(clip.receiving?.of)
            || Number(context?.order?.quantity)
            || Number(context?.estimate?.quantity) || 1}
          received={Number(clip.receiving?.count) || 0}
          edit={fixReceipt}
          onSaved={fetchClip}
          onClose={() => { setReceiving(false); setFixReceipt(null); fetchClip(); }}
        />
      )}
      {tagPanel && (
        <ClipTagsPanel clip={clip} onUpdate={fetchClip}
          onClose={() => setTagPanel(false)} />
      )}

      {/* CL-2 — a new tag for this clip: the shared pre-print form */}
      {tagCreate && (
        <MaterialTagForm
          projectId={clip.project_id}
          source="clip"
          body={{ clip_id: clip.id }}
          title={clip.title || ''}
          room={clip.room || ''}
          roomId={clip.room_id || null}
          location={clip.room_location || ''}
          tradePrefix={clip.spec_category || ''}
          trade={clip.last_tag_trade || ''}
          tagName={clip.tag_name || ''}
          tagNote={clip.tag_note || ''}
          imageCount={(clip.images || []).length}
          existingTag={reprintTag}
          onClose={() => { setTagCreate(false); setReprintTag(null); }}
        />
      )}

    </div>
  );
}